/**
 * Iter-206p / Iter-206r — CRA Scope Audit (CEO/ADMIN).
 *
 * Lists invoices whose persisted Monthly CRA is likely "wide" (not filtered
 * to the BC's profiles). Iter-206r — broadened detection: catches ALL
 * invoices with a `po_number` but empty `po_role_names`, even when the BC
 * was never uploaded into `purchase_orders` (= single-BC visible count but
 * still legacy invoice). Each row shows the mission's PO count so the CEO
 * sees the difference between a true multi-BC mission and a single-BC one
 * that just needs a metadata back-fill.
 *
 * When the backend cannot auto-resolve the BC roles (e.g. PO never uploaded),
 * a modal appears asking the CEO to pick the mission roles manually. The
 * mission's role list is fetched on-demand.
 */
import React, { useEffect, useState } from "react";
import { Loader2, AlertTriangle, RefreshCw, CheckCircle2, X, Eye, Ban, Trash2, Sliders } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function CRAScopeAudit() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [fixingId, setFixingId] = useState(null);
  const [fixingAll, setFixingAll] = useState(false);
  const [fixedIds, setFixedIds] = useState(new Set());
  // Iter-206r — manual override modal
  const [overrideTarget, setOverrideTarget] = useState(null); // { inv, missionRoles, selected: [] }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/cra-scope-audit");
      setItems(r.data?.items || []);
      setFixedIds(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const fixOne = async (inv) => {
    setFixingId(inv.invoice_id);
    try {
      const r = await api.post(`/invoices/${inv.invoice_id}/regenerate-cra`);
      const roles = (r.data?.po_role_names || []).join(", ") || "—";
      const source = r.data?.diagnostics?.resolution_source || "—";
      toast.success(`CRA ${inv.number} régénéré · source: ${source} · rôles: ${roles}`);
      // Iter-206t — refresh cra_pdf_built_at locally so the next "Voir" busts
      // the browser cache and shows the freshly-built PDF (not the stale one).
      const newBuiltAt = new Date().toISOString();
      setItems(prev => prev.map(it => it.invoice_id === inv.invoice_id
        ? { ...it, cra_pdf_built_at: newBuiltAt, po_role_names: r.data?.po_role_names || [], has_persisted_cra: true }
        : it));
      setFixedIds(prev => new Set([...prev, inv.invoice_id]));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Iter-206r — open the manual-override modal when the backend cannot resolve.
      if (detail && typeof detail === "object" && detail.code === "po_role_names_unresolved") {
        try {
          const m = await api.get(`/missions/${inv.mission_id}`);
          const missionRoles = (m.data?.roles || []).map(r => r.name).filter(Boolean);
          setOverrideTarget({ inv, missionRoles, selected: [], diagnostics: detail.diagnostics || null });
        } catch {
          setOverrideTarget({ inv, missionRoles: detail.mission_role_names || [], selected: [], diagnostics: detail.diagnostics || null });
        }
      } else {
        toast.error(`${inv.number}: ${typeof detail === "string" ? detail : (detail?.message || "Échec")}`);
      }
    } finally { setFixingId(null); }
  };

  // Iter-206t — force the manual override modal even when the backend auto-resolution
  // would succeed. Useful when the line_roles fallback would produce a wide CRA
  // (legacy invoices with lines spanning multiple BCs).
  const openManualOverride = async (inv) => {
    try {
      const m = await api.get(`/missions/${inv.mission_id}`);
      const missionRoles = (m.data?.roles || []).map(r => r.name).filter(Boolean);
      setOverrideTarget({ inv, missionRoles, selected: [], diagnostics: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du chargement des rôles de la mission");
    }
  };

  // Iter-206t — physically delete the persisted CRA file + clear DB references.
  // After delete, the next regenerate starts from scratch.
  const [deletingCraId, setDeletingCraId] = useState(null);
  const deleteCRA = async (inv) => {
    if (!window.confirm(`Supprimer physiquement le CRA persisté de la facture ${inv.number} ?\n\nLe fichier sera retiré du disque et les références (po_role_names, cra_pdf_path) effacées. Vous pourrez ensuite régénérer un nouveau CRA depuis zéro (et utiliser la sélection manuelle des rôles si nécessaire).`)) return;
    setDeletingCraId(inv.invoice_id);
    try {
      const r = await api.delete(`/invoices/${inv.invoice_id}/cra-pdf`);
      toast.success(`CRA supprimé${r.data?.file_deleted ? " (fichier retiré du disque)" : ""}. Régénérez maintenant.`);
      setItems(prev => prev.map(it => it.invoice_id === inv.invoice_id
        ? { ...it, has_persisted_cra: false, cra_pdf_built_at: null, po_role_names: [] }
        : it));
      // Remove from fixedIds so the row goes back to "to fix" state and the action buttons reappear.
      setFixedIds(prev => {
        const next = new Set(prev);
        next.delete(inv.invoice_id);
        return next;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de suppression");
    } finally { setDeletingCraId(null); }
  };

  // Iter-206s — view the persisted CRA via authenticated blob download.
  // A raw <a href> would fail in PROD (no Authorization header → 401 → home).
  const [viewingCraId, setViewingCraId] = useState(null);
  const viewCRA = async (inv) => {
    setViewingCraId(inv.invoice_id);
    try {
      const r = await api.get(`/invoices/${inv.invoice_id}/cra-pdf`, {
        responseType: "blob",
        params: { v: inv.cra_pdf_built_at || Date.now() },
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec d'ouverture du CRA");
    } finally {
      setViewingCraId(null);
    }
  };

  // Iter-206s — mark an invoice as false-positive (will be excluded from the audit list).
  const markFalsePositive = async (inv) => {
    if (!window.confirm(`Marquer la facture ${inv.number} comme faux positif ?\n\nElle ne réapparaîtra plus dans cet audit (la BDD garde la trace). Vous pouvez l'annuler depuis le détail de la facture.`)) return;
    try {
      await api.post(`/invoices/${inv.invoice_id}/cra-audit-ignore`);
      toast.success(`${inv.number} marqué comme faux positif.`);
      setItems(prev => prev.filter(it => it.invoice_id !== inv.invoice_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const submitOverride = async () => {
    if (!overrideTarget || overrideTarget.selected.length === 0) {
      toast.error("Sélectionnez au moins un rôle");
      return;
    }
    setFixingId(overrideTarget.inv.invoice_id);
    try {
      const r = await api.post(`/invoices/${overrideTarget.inv.invoice_id}/regenerate-cra`, {
        force_po_role_names: overrideTarget.selected,
      });
      toast.success(`CRA ${overrideTarget.inv.number} régénéré (rôles forcés : ${r.data.po_role_names.join(", ")})`);
      // Iter-206t — refresh cache-busting + persisted roles locally.
      const newBuiltAt = new Date().toISOString();
      setItems(prev => prev.map(it => it.invoice_id === overrideTarget.inv.invoice_id
        ? { ...it, cra_pdf_built_at: newBuiltAt, po_role_names: r.data?.po_role_names || [], has_persisted_cra: true }
        : it));
      setFixedIds(prev => new Set([...prev, overrideTarget.inv.invoice_id]));
      setOverrideTarget(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Échec"));
    } finally { setFixingId(null); }
  };

  const fixAll = async () => {
    if (!window.confirm(`Corriger les ${items.length} CRA listés ?\n\nChaque CRA sera régénéré filtré par les rôles de son BC. Les factures qui nécessitent une saisie manuelle seront ignorées.`)) return;
    setFixingAll(true);
    let ok = 0, ko = 0, manual = 0;
    for (const inv of items) {
      if (fixedIds.has(inv.invoice_id)) continue;
      try {
        await api.post(`/invoices/${inv.invoice_id}/regenerate-cra`);
        ok++;
        setFixedIds(prev => new Set([...prev, inv.invoice_id]));
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (d?.code === "po_role_names_unresolved") manual++; else ko++;
      }
    }
    setFixingAll(false);
    const msg = `${ok} régénérés${manual ? ` · ${manual} à corriger manuellement` : ""}${ko ? ` · ${ko} en échec` : ""}.`;
    if (ko === 0 && manual === 0) toast.success(msg); else toast.warning(msg);
    setTimeout(load, 800);
  };

  const toggleRole = (role) => {
    setOverrideTarget(prev => {
      if (!prev) return prev;
      const has = prev.selected.includes(role);
      return { ...prev, selected: has ? prev.selected.filter(r => r !== role) : [...prev.selected, role] };
    });
  };

  return (
    <div className="space-y-6 px-2 md:px-6 lg:px-10 py-2" data-testid="cra-scope-audit-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
          admin · système · maintenance · cra scope audit
        </div>
        <h1 className="text-2xl font-bold font-heading text-zinc-900">CRA Scope Audit</h1>
        <p className="text-sm text-zinc-600 max-w-4xl leading-relaxed mt-2">
          Liste des factures dont le Monthly CRA persistant n'a pas été filtré par les rôles
          du BC. Ces CRA peuvent contenir des heures issues de profils hors périmètre.
          Cliquez sur <strong>Régénérer</strong> pour reconstruire le CRA filtré par le BC de
          la facture, ou sur <strong>Tout corriger</strong> pour traiter la liste en lot.
          Si la résolution automatique échoue (BC jamais uploadé dans Purchase Orders), une
          fenêtre vous demande de sélectionner manuellement les rôles concernés.
        </p>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-zinc-600">
          {loading ? "Chargement…" : (
            <>
              <span className="font-mono">{items.length}</span> facture(s) à corriger
              {fixedIds.size > 0 && (
                <span className="ml-2 text-emerald-700">· {fixedIds.size} corrigée(s)</span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="cra-audit-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-[11px] font-mono uppercase tracking-wider text-zinc-700 rounded-md hover:bg-zinc-50"
          >
            <RefreshCw size={12} /> Actualiser
          </button>
          {items.length > 0 && (
            <button
              onClick={fixAll}
              disabled={fixingAll}
              data-testid="cra-audit-fix-all-btn"
              className="inline-flex items-center gap-2 px-4 h-9 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              {fixingAll ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {fixingAll ? "Correction en cours…" : "Tout corriger"}
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-zinc-400" size={28} />
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-8 text-center" data-testid="cra-audit-empty">
          <CheckCircle2 className="text-emerald-600 mx-auto mb-3" size={32} />
          <div className="font-semibold text-emerald-900 text-lg">Tout est propre.</div>
          <div className="text-sm text-emerald-700 mt-2">
            Aucune facture avec un Monthly CRA non filtré n'a été détectée.
          </div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-zinc-600">
                  <th className="px-5 py-3 whitespace-nowrap">Facture</th>
                  <th className="px-5 py-3 whitespace-nowrap">Période</th>
                  <th className="px-5 py-3 whitespace-nowrap">BC</th>
                  <th className="px-5 py-3">Mission</th>
                  <th className="px-5 py-3 text-center whitespace-nowrap">Nb BC</th>
                  <th className="px-5 py-3 text-right whitespace-nowrap">Montant TTC</th>
                  <th className="px-5 py-3 whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map(i => {
                  const fixed = fixedIds.has(i.invoice_id);
                  const isMulti = (i.mission_po_count || 0) >= 2;
                  return (
                    <tr
                      key={i.invoice_id}
                      data-testid={`cra-audit-row-${i.invoice_id}`}
                      className={`border-b border-zinc-100 last:border-b-0 ${fixed ? "bg-emerald-50" : "hover:bg-zinc-50/50"}`}
                    >
                      <td className="px-5 py-3.5 font-medium text-zinc-900 whitespace-nowrap">{i.number || "—"}</td>
                      <td className="px-5 py-3.5 text-zinc-600 font-mono text-xs whitespace-nowrap">
                        {String(i.month || "").padStart(2, "0")}/{i.year || "—"}
                      </td>
                      <td className="px-5 py-3.5 text-zinc-700 font-mono text-xs whitespace-nowrap">{i.po_number || "—"}</td>
                      <td className="px-5 py-3.5 text-zinc-700 max-w-md truncate" title={i.mission_title}>
                        {i.mission_title || "—"}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono ${isMulti ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-700"}`}>
                          {i.mission_po_count || 0}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right text-zinc-700 font-mono text-xs whitespace-nowrap">
                        {i.total_ttc != null
                          ? `${Number(i.total_ttc).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${i.currency || ""}`
                          : "—"}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Iter-206t — "Voir" stays visible even after a fix so the operator
                              can verify the freshly regenerated CRA. Cache-busted via cra_pdf_built_at. */}
                          <button
                            onClick={() => viewCRA(i)}
                            disabled={viewingCraId === i.invoice_id}
                            data-testid={`cra-audit-view-${i.invoice_id}`}
                            title="Voir le CRA persisté actuel"
                            className="inline-flex items-center gap-1.5 px-2.5 h-8 border border-zinc-200 text-[11px] font-mono uppercase text-zinc-600 rounded-md hover:bg-zinc-50 disabled:opacity-50"
                          >
                            {viewingCraId === i.invoice_id ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />} Voir
                          </button>
                          {fixed && (
                            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                              <CheckCircle2 size={12} /> Corrigé
                            </span>
                          )}
                          <button
                            onClick={() => fixOne(i)}
                            disabled={fixingId === i.invoice_id || fixingAll}
                            data-testid={`cra-audit-fix-${i.invoice_id}`}
                            title="Régénérer automatiquement le CRA en filtrant par les rôles du BC"
                            className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 text-[11px] font-mono uppercase tracking-wider text-zinc-700 rounded-md hover:bg-zinc-50 disabled:opacity-50"
                          >
                            {fixingId === i.invoice_id ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Régénérer
                          </button>
                          {/* Iter-206t — toujours-visible : ouvre la modale de sélection manuelle même
                              si l'auto-résolution réussirait (pour contourner le fallback line_roles
                              qui produit un CRA "large" pour les factures legacy multi-BC). */}
                          <button
                            onClick={() => openManualOverride(i)}
                            disabled={fixingId === i.invoice_id || fixingAll}
                            data-testid={`cra-audit-manual-${i.invoice_id}`}
                            title="Forcer la sélection manuelle des rôles (utile si la régénération auto produit un CRA trop large)"
                            className="inline-flex items-center gap-1.5 px-2.5 h-8 border border-indigo-200 bg-indigo-50 text-[11px] font-mono uppercase text-indigo-800 rounded-md hover:bg-indigo-100 disabled:opacity-50"
                          >
                            <Sliders size={11} /> Manuel
                          </button>
                          {/* Iter-206t — physically delete the persisted CRA file + clear DB refs. */}
                          {i.has_persisted_cra && (
                            <button
                              onClick={() => deleteCRA(i)}
                              disabled={deletingCraId === i.invoice_id || fixingAll}
                              data-testid={`cra-audit-delete-${i.invoice_id}`}
                              title="Supprimer physiquement le CRA persisté (fichier + références BDD)"
                              className="inline-flex items-center gap-1.5 px-2.5 h-8 border border-red-200 bg-red-50 text-[11px] font-mono uppercase text-red-700 rounded-md hover:bg-red-100 disabled:opacity-50"
                            >
                              {deletingCraId === i.invoice_id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />} Supprimer
                            </button>
                          )}
                          {!fixed && (
                            <button
                              onClick={() => markFalsePositive(i)}
                              disabled={fixingId === i.invoice_id || fixingAll}
                              data-testid={`cra-audit-false-positive-${i.invoice_id}`}
                              title="Marquer comme faux positif (sera ignoré dans l'audit)"
                              className="inline-flex items-center gap-1.5 px-2.5 h-8 border border-amber-200 bg-amber-50 text-[11px] font-mono uppercase text-amber-800 rounded-md hover:bg-amber-100 disabled:opacity-50"
                            >
                              <Ban size={11} /> Faux positif
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-md border border-amber-200 bg-amber-50 p-5 text-xs text-amber-900 leading-relaxed max-w-4xl">
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <div className="space-y-1.5">
            <div><strong>Que fait "Régénérer" ?</strong> Le serveur tente d'identifier les rôles du BC dans cet ordre :</div>
            <ol className="list-decimal list-inside ml-2 space-y-0.5">
              <li>Rôles déjà persistés sur la facture (champ <code>po_role_names</code>)</li>
              <li>Rôles du document BC matching <code>mission_id + po_number</code> (case-insensitive)</li>
              <li>Rôles présents sur les <code>lines</code> de la facture (fallback historique)</li>
            </ol>
            <div>Si aucune source ne renvoie de rôle, une fenêtre vous propose de les saisir manuellement.</div>
            <div className="pt-2"><strong>Le CRA régénéré reste large ?</strong> Cela signifie que le fallback <code>line_roles</code> (étape 3) renvoie plusieurs rôles. Solutions :</div>
            <ul className="list-disc list-inside ml-2 space-y-0.5">
              <li><strong>Manuel</strong> : ouvre la modale de sélection des rôles, force le filtrage.</li>
              <li><strong>Supprimer</strong> : retire physiquement le CRA du disque + efface <code>po_role_names</code>. Puis cliquez Régénérer/Manuel pour repartir de zéro.</li>
            </ul>
            <div className="pt-1"><strong>Pictogramme "Nb BC"</strong> : nombre de documents BC uploadés pour la mission. Une valeur ≥ 2 = vraie mission multi-BC.</div>
          </div>
        </div>
      </div>

      {/* Iter-206r — manual override modal ----------------------------------- */}
      {overrideTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setOverrideTarget(null)}>
          <div className="bg-white rounded-lg max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="cra-audit-override-modal">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-heading font-bold text-zinc-900">Sélection manuelle des rôles</h2>
                <p className="text-xs text-zinc-500 mt-1">
                  Facture <strong>{overrideTarget.inv.number}</strong> · BC <code className="font-mono text-zinc-700">{overrideTarget.inv.po_number}</code>
                </p>
              </div>
              <button onClick={() => setOverrideTarget(null)} data-testid="cra-audit-override-close" className="text-zinc-400 hover:text-zinc-700">
                <X size={20} />
              </button>
            </div>
            <p className="text-xs text-zinc-600 leading-relaxed">
              Le BC <code>{overrideTarget.inv.po_number}</code> n'a pas été retrouvé dans Purchase Orders.
              Sélectionnez les rôles de la mission qui sont couverts par ce BC — seuls les
              intervenants assignés à ces rôles apparaîtront dans le Monthly CRA régénéré.
            </p>
            {overrideTarget.missionRoles.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
                Aucun rôle trouvé sur la mission. Vérifiez la configuration des rôles avant de régénérer.
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="cra-audit-override-roles">
                {overrideTarget.missionRoles.map(role => (
                  <label key={role} className="flex items-center gap-2.5 p-2 border border-zinc-200 rounded-md hover:bg-zinc-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overrideTarget.selected.includes(role)}
                      onChange={() => toggleRole(role)}
                      data-testid={`cra-audit-override-role-${role.replace(/\s+/g, "-").toLowerCase()}`}
                      className="rounded"
                    />
                    <span className="text-sm text-zinc-800">{role}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
              <button onClick={() => setOverrideTarget(null)} className="text-xs font-mono uppercase text-zinc-500 hover:text-zinc-800">
                Annuler
              </button>
              <button
                onClick={submitOverride}
                disabled={overrideTarget.selected.length === 0 || fixingId === overrideTarget.inv.invoice_id}
                data-testid="cra-audit-override-submit"
                className="inline-flex items-center gap-2 px-4 h-9 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
              >
                {fixingId === overrideTarget.inv.invoice_id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Régénérer avec ces rôles
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
